import React from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEmpty, map, filter, isEqual, find } from 'lodash';
import { Bert } from 'meteor/themeteorchef:bert';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import TablePagination from '../TablePagination';

import FontawesomeIconList from '../../../styles/fontawesomeIconList';

import methodPromise from '../../../modules/method-promise-wrapper';

import { LIBRARY_ICON_TYPES } from '../../../api/AnnotationTemplates/AnnotationTemplates';

const ShowListIconsFontawesome = ({
  openModal,
  setOpenModal,
  handleIconSelected,
  selectedIcon,
  startLibrary,
  svg,
  startCategorySvg
}) => {
  const [libraryIcons, setLibraryIcons] = React.useState(startLibrary);
  const [svgCategoryListAPI, setSVGCategoryListAPI] = React.useState('');
  const [svgCategoryList, setSVGCategoryList] = React.useState([]);
  const [svgCategory, setSVGCategory] = React.useState({});
  const [svgList, setSVGList] = React.useState([]);

  const itemsPerPage = 90;
  const totalCount =
    libraryIcons === LIBRARY_ICON_TYPES.FONTAWESOME ? FontawesomeIconList.length : svgList.length;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const elmentFontAwesome = find(FontawesomeIconList, iconElement =>
    isEqual(iconElement.name, selectedIcon)
  );
  const elmentSvg = find(svgList, iconElement => isEqual(iconElement.url, svg));
  const indexStartElement = isEqual(LIBRARY_ICON_TYPES.FONTAWESOME, startLibrary)
    ? FontawesomeIconList.indexOf(elmentFontAwesome)
    : svgList.indexOf(elmentSvg);
  const startPage = Math.round(indexStartElement / 90) + 1;
  const startIndexFilter = itemsPerPage * startPage;
  const startPrevIndexFilter = itemsPerPage * (startPage - 1);

  const startFontAwesomeToShow = filter(
    FontawesomeIconList,
    (element, index) => index >= startPrevIndexFilter && index < startIndexFilter
  );
  const startSvgToShow = filter(
    svgList,
    (element, index) => index >= startPrevIndexFilter && index < startIndexFilter
  );
  const startElementsToShow =
    libraryIcons === LIBRARY_ICON_TYPES.FONTAWESOME ? startFontAwesomeToShow : startSvgToShow;

  const [currentPage, setCurrentPage] = React.useState(startPage);
  const [elementsToShow, setElementsToShow] = React.useState(startElementsToShow);
  const [iconState, setIconState] = React.useState(selectedIcon);
  const [svgState, setSvgState] = React.useState(svg);

  const themeButtons = createTheme({
    palette: {
      primary: {
        main: '#9e9e9e'
      },
      secondary: {
        main: '#424242'
      }
    }
  });

  const changePage = pageNumber => {
    const indexFilter = itemsPerPage * pageNumber;
    const prevIndexFilter = itemsPerPage * (pageNumber - 1);
    const newElementsFontawesome = filter(
      FontawesomeIconList,
      (element, index) => index >= prevIndexFilter && index < indexFilter
    );
    const newElementsSvg = filter(
      svgList,
      (element, index) => index >= prevIndexFilter && index < indexFilter
    );
    const newElementsToShow =
      libraryIcons === LIBRARY_ICON_TYPES.FONTAWESOME ? newElementsFontawesome : newElementsSvg;
    setElementsToShow(newElementsToShow);
    setCurrentPage(pageNumber);
  };

  const handleChangeLibrary = library => {
    if (isEqual(library, LIBRARY_ICON_TYPES.FONTAWESOME)) {
      setElementsToShow(startFontAwesomeToShow);
    }
    if (isEqual(library, LIBRARY_ICON_TYPES.SVG)) {
      setElementsToShow(startSvgToShow);
    }
    setIconState(selectedIcon);
    setSvgState(svg);
    setCurrentPage(startPage);
    setLibraryIcons(library);
  };

  const handleSearchIcon = value => {
    if (isEmpty(value)) setElementsToShow(startElementsToShow);
    else {
      const newElementsToShow = filter(FontawesomeIconList, element =>
        element.name.includes(value)
      );
      setElementsToShow(newElementsToShow);
    }
  };

  const getSVGList = async svgCategoryURL => {
    try {
      const response = await fetch(
        'https://sd1.ipsgeoprocesing.ipsilum.com/MG/utilities/list_files_folders',
        {
          method: 'POST',
          body: JSON.stringify({
            path: svgCategoryURL,
            beyond_this_folder: null,
            only_folders: null,
            only_files: null,
            filter_type_file: null
          }),
          headers: { 'Content-Type': 'application/json' },
          mode: 'cors',
          credentials: 'omit',
          referrerPolicy: 'no-referrer'
        }
      );
      const answer = await response.json();
      let filterSvgList = answer.data;
      if (filterSvgList.length > itemsPerPage) {
        filterSvgList = filter(answer.data, (element, index) => index < itemsPerPage);
      }
      setElementsToShow(filterSvgList);
      setSVGList(answer.data);
    } catch (error) {
      Bert.alert('SVG list of category selected not found', 'warning');
    }
  };

  const getSVGCategoryList = async () => {
    try {
      const response = await fetch(
        'https://sd1.ipsgeoprocesing.ipsilum.com/MG/utilities/list_files_folders',
        {
          method: 'POST',
          body: JSON.stringify({
            path: svgCategoryListAPI,
            beyond_this_folder: null,
            only_folders: null,
            only_files: null,
            filter_type_file: null
          }),
          headers: { 'Content-Type': 'application/json' },
          mode: 'cors',
          credentials: 'omit',
          referrerPolicy: 'no-referrer'
        }
      );
      const answer = await response.json();

      setSVGCategoryList(answer.data);
    } catch (error) {
      Bert.alert('SVG category list not found', 'warning');
    }
  };

  const callAPISettings = async () => {
    try {
      const APISettings = await methodPromise('admin.fetchAPISetting');
      if (APISettings.length > 0) {
        setSVGCategoryListAPI(APISettings[0].svgCategoryListAPI);
      }
    } catch (error) {
      Bert.alert('SVG category url not found', 'warning');
    }
  };

  const handleCategoryChange = newCategoryName => {
    svgCategoryList.forEach(category => {
      if (newCategoryName === category.name) {
        setSVGCategory({ name: category.name, url: category.url });
        getSVGList(category.url);
      }
    });
  };

  React.useEffect(() => {
    callAPISettings();
  }, []);

  React.useEffect(() => {
    if (!isEmpty(svgCategoryListAPI)) getSVGCategoryList();
  }, [svgCategoryListAPI]);

  React.useEffect(() => {
    if (!isEmpty(svgCategoryList) && !isEmpty(startCategorySvg))
      handleCategoryChange(startCategorySvg);
  }, [svgCategoryList]);

  React.useEffect(() => {
    if (libraryIcons === LIBRARY_ICON_TYPES.FONTAWESOME) {
      if (!isEmpty(iconState) && !isEqual(selectedIcon, iconState))
        handleIconSelected(iconState, libraryIcons);
    }
    if (libraryIcons === LIBRARY_ICON_TYPES.SVG) {
      if (!isEmpty(svgState) && !isEqual(svg, svgState) && !isEqual(selectedIcon, iconState))
        handleIconSelected(
          { name: iconState, url: svgState, category: svgCategory.name },
          libraryIcons
        );
    }
  }, [iconState, svgState]);

  return (
    <Modal open={openModal} onClose={() => setOpenModal(false)}>
      <Box
        style={{
          zIndex: 1800,
          position: 'absolute',
          backgroundColor: 'white',
          height: '80%',
          width: '30%',
          top: '10%',
          left: '35%'
        }}
      >
        <Box height="5%" marginTop="5%" marginBottom="5%">
          <Stack direction="row" display="flex" justifyContent="space-around">
            <ThemeProvider theme={themeButtons}>
              <Button
                variant={libraryIcons === LIBRARY_ICON_TYPES.FONTAWESOME ? 'contained' : 'outlined'}
                color={libraryIcons === LIBRARY_ICON_TYPES.FONTAWESOME ? 'primary' : 'secondary'}
                onClick={() => handleChangeLibrary(LIBRARY_ICON_TYPES.FONTAWESOME)}
                size="small"
              >
                Fontawesome
              </Button>

              <Button
                variant={libraryIcons === LIBRARY_ICON_TYPES.SVG ? 'contained' : 'outlined'}
                color={libraryIcons === LIBRARY_ICON_TYPES.SVG ? 'primary' : 'secondary'}
                onClick={() => handleChangeLibrary(LIBRARY_ICON_TYPES.SVG)}
                size="small"
              >
                Custom Icons
              </Button>
            </ThemeProvider>
          </Stack>
        </Box>
        <Box width="100%">
          {libraryIcons === LIBRARY_ICON_TYPES.FONTAWESOME && (
            <>
              <Stack direction="row" display="flex" justifyContent="center">
                <TextField
                  label="Search icon"
                  onChange={event => handleSearchIcon(event.target.value)}
                  size="small"
                  style={{ marginBottom: '2%', justifyContent: 'center', width: '90%' }}
                />
              </Stack>
              <Stack direction="row" display="block" marginLeft="4%">
                {!isEmpty(elementsToShow) &&
                  map(elementsToShow, element => {
                    return (
                      <Button
                        key={element.name}
                        size="small"
                        color={isEqual(iconState, element.name) ? 'success' : 'primary'}
                        variant={isEqual(iconState, element.name) ? 'contained' : 'outlined'}
                        onClick={() => {
                          setIconState(element.name);
                        }}
                      >
                        <FontAwesomeIcon key={element.name} icon={element.name} />
                      </Button>
                    );
                  })}
              </Stack>
            </>
          )}
          {libraryIcons === LIBRARY_ICON_TYPES.SVG && (
            <>
              <Stack direction="row" justifyContent="center">
                <FormControl sx={{ mb: 2, width: '90%' }}>
                  <InputLabel id="demo-select-label">Category</InputLabel>
                  <Select
                    id="svgCategory"
                    value={!isEmpty(svgCategory) ? svgCategory.name : ''}
                    label="Category"
                    onChange={event => handleCategoryChange(event.target.value.trim())}
                    size="small"
                  >
                    <MenuItem value="">-</MenuItem>
                    {map(svgCategoryList, ({ name }) => (
                      <MenuItem value={name.trim()} key={name.trim()}>
                        {name.trim()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              {!isEmpty(svgList) && (
                <Stack direction="row" marginTop="2%" marginLeft="4%" display="block">
                  {map(elementsToShow, ({ url, name }) => (
                    <Button
                      key={url}
                      color={isEqual(iconState, name) ? 'success' : 'primary'}
                      variant={isEqual(iconState, name) ? 'contained' : 'outlined'}
                      onClick={() => {
                        setSvgState(url);
                        setIconState(name);
                      }}
                      size="small"
                    >
                      <img
                        src={url}
                        alt={name}
                        style={{ marginRight: '5px', width: '20px', height: '20px' }}
                      />
                    </Button>
                  ))}
                </Stack>
              )}
            </>
          )}
        </Box>
        <Box marginTop="5%" marginRight="5%">
          <Stack direction="row" display="flex" justifyContent="right">
            <Stack direction="column" marginTop="2%" marginBottom="0%" marginRight="2%">
              <Typography>{`${currentPage} - ${totalPages}`}</Typography>
            </Stack>
            <Stack direction="column">
              <TablePagination
                currentPage={currentPage}
                isPaginationOn
                itemsPerPage={itemsPerPage}
                stretchView
                totalCount={totalCount}
                changePage={changePage}
              />
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};

ShowListIconsFontawesome.defaultProps = {
  selectedIcon: '',
  startLibrary: 'fontawesome',
  svg: '',
  startCategorySvg: ''
};
ShowListIconsFontawesome.propTypes = {
  openModal: PropTypes.bool.isRequired,
  setOpenModal: PropTypes.func.isRequired,
  handleIconSelected: PropTypes.func.isRequired,
  selectedIcon: PropTypes.string,
  startLibrary: PropTypes.string,
  svg: PropTypes.string,
  startCategorySvg: PropTypes.string
};
export default ShowListIconsFontawesome;
